/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchEmploymentHistoryByEmployeeId,
  createEmploymentHistory,
  updateEmploymentHistory,
  deleteEmploymentHistory,
  clearEmploymentHistory,
  checkEmployeeCompleteness,
} from "../../store/slices/employeeEmploymentHistorySlice";
import { fetchEmployeeById } from "../../store/slices/employeeDetailSlice";
import "bootstrap-icons/font/bootstrap-icons.css";

const EMPTY_FORM = {
  companyName: "",
  companyAddress: "",
  employeeType: "Permanent",
  position: "",
  startDate: "",
  endDate: "",
  reasonForLeaving: "",
};

const EmployeeHistory = () => {
  const dispatch = useDispatch();
  const { id: employeeId } = useParams();

  const { histories = [], status = "idle" } = useSelector((state) => state.employeeHistory || {});

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  const employeeTypes = [
    { label: "Permanent", value: "Permanent" },
    { label: "Probationary", value: "Probationary" },
    { label: "Full-time", value: "Full-time" },
    { label: "Part-time", value: "Part-time" },
    { label: "Contract", value: "Contract" },
    { label: "Temporary", value: "Temporary" },
    { label: "Seasonal", value: "Seasonal" },
    { label: "On-call", value: "On-call" },
    { label: "Intern", value: "Intern" },
    { label: "Freelancer", value: "Freelancer" },
  ];

  useEffect(() => {
    if (employeeId) dispatch(fetchEmploymentHistoryByEmployeeId(employeeId));
    return () => dispatch(clearEmploymentHistory());
  }, [employeeId, dispatch]);

  const showStatus = (text, type = "success") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handleEditInitiate = (item) => {
    setEditingId(item.id);
    setFormData({
      companyName: item.companyName || "",
      companyAddress: item.companyAddress || "",
      employeeType: item.employeeType || "", 
      position: item.position || "",
      startDate: item.startDate?.slice(0, 10) || "",
      endDate: item.endDate?.slice(0, 10) || "",
      reasonForLeaving: item.reasonForLeaving || "",
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setStatusMsg({ text: "", type: "" });
  };

  const handleSave = async (id) => {
    if (!formData.companyName || !formData.position || !formData.startDate) {
      showStatus("Required fields missing.", "danger");
      return;
    }

    const start = new Date(formData.startDate);
    const end = formData.endDate ? new Date(formData.endDate) : null;
    if (end && start > end) {
      showStatus("Check date sequence.", "danger");
      return;
    }

    setIsProcessing(true);
    try {
      const payload = { ...formData, employeeId };

      if (id && id !== "new") {
        await dispatch(updateEmploymentHistory({ id, data: payload })).unwrap();
        showStatus("Updated successfully!");
      } else {
        await dispatch(createEmploymentHistory(payload)).unwrap();
        showStatus("Added successfully!");
      }

      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();

      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      dispatch(fetchEmploymentHistoryByEmployeeId(employeeId));
      dispatch(fetchEmployeeById(employeeId));
    } catch (err) {
      showStatus(err?.message || "Operation failed.", "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    
    setIsProcessing(true);
    try {
      await dispatch(deleteEmploymentHistory(id)).unwrap();
      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();

      showStatus("Deleted successfully!");
      dispatch(fetchEmploymentHistoryByEmployeeId(employeeId));
    } catch (err) {
      showStatus("Failed to delete record.", "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const getDuration = (start, end) => {
    if (!start) return "N/A";
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();

    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();
    let days = endDate.getDate() - startDate.getDate();

    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
      days += prevMonth.getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    if (days > 0 || parts.length === 0) parts.push(`${days}d`);
    return parts.join(" ");
  };

  const inputStyle = { 
    height: "32px",
    border: "1px solid rgba(0,0,0,0.1)", 
    borderRadius: "8px", 
    padding: "6px 10px", 
    fontSize: "0.8rem",
    backgroundColor: "#ffffff",
  };

  const headerStyle = { 
    backgroundColor: "#1e293b", 
    color: "#fff", 
    fontSize: "0.7rem", 
    fontWeight: "700", 
    letterSpacing: "0.05em",
    border: "none"
  };

  const isLoading = isProcessing;

  return (
    <div className="container-fluid py-2 px-0">
      <style>{`
        .extra-small { font-size: 0.65rem; letter-spacing: 0.05em; }
        .progress-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); z-index: 100; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
      `}</style>

      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        {isLoading && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <div className="d-flex justify-content-end align-items-center px-3 py-2 rounded-2 mb-3" style={{ backgroundColor: "#1e293b", minHeight: '44px' }}>
          <div className="d-flex align-items-center gap-3">
            {statusMsg.text && (
              <span className={`fw-bold text-uppercase d-flex align-items-center text-${statusMsg.type}`} style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                <i className={`bi ${statusMsg.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                {statusMsg.text}
              </span>
            )}
            
            {editingId !== "new" && (
              <button 
                className="btn btn-sm d-flex align-items-center px-3 text-white border-0 shadow-none" 
                style={{ backgroundColor: "#198754", borderRadius: "6px", fontWeight: "600", fontSize: '0.75rem', height: '28px' }} 
                onClick={() => { setEditingId("new"); setFormData({...EMPTY_FORM}); }}
                disabled={isLoading}
              >
                <i className="bi bi-plus-circle me-2"></i> Add
              </button>
            )}
          </div>
        </div>

        <div className="table-responsive rounded-2" style={{ border: "1px solid #dee2e6", overflow: "hidden" }}>
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th className="py-3 ps-4" style={headerStyle}>COMPANY</th>
                <th className="py-3" style={headerStyle}>TYPE</th>
                <th className="py-3" style={headerStyle}>POSITION</th>
                <th className="py-3" style={headerStyle}>DATES</th>
                <th className="py-3" style={headerStyle}>DURATION</th>
                <th className="py-3" style={headerStyle}>REASON</th>
                <th className="py-3 pe-4 text-end" style={{ ...headerStyle, width: "110px" }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody style={{ borderTop: "0" }}>
              {editingId === "new" && (
                <tr className="bg-primary bg-opacity-10">
                  {renderEditRow("new", formData, handleChange, handleSave, handleCancel, employeeTypes, isLoading, inputStyle)}
                </tr>
              )}

              {histories.length > 0 ? (
                histories.map((h) => (
                  <tr key={h.id} className={editingId === h.id ? "bg-primary bg-opacity-10" : ""}>
                    {editingId === h.id ? (
                      renderEditRow(h.id, formData, handleChange, handleSave, handleCancel, employeeTypes, isLoading, inputStyle)
                    ) : (
                      <>
                        <td className="ps-4 py-3">
                          <div className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>{h.companyName}</div>
                          <div className="text-muted" style={{ fontSize: "0.65rem" }}>{h.companyAddress}</div>
                        </td>
                        <td>
                          <span className="badge border bg-light text-dark border-secondary-subtle text-uppercase" style={{ fontSize: "0.6rem" }}>
                            {h.employeeType}
                          </span>
                        </td>
                        <td style={{ fontSize: "0.8rem" }} className="fw-medium">{h.position}</td>
                        <td style={{ fontSize: "0.75rem" }} className="text-muted">
                          {h.startDate?.slice(0, 10)} — {h.endDate ? h.endDate.slice(0, 10) : <span className="text-primary fw-bold">Present</span>}
                        </td>
                        <td>
                           <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10" style={{ fontSize: "0.65rem" }}>
                            {getDuration(h.startDate, h.endDate)}
                          </span>
                        </td>
                        <td className="text-muted text-wrap" style={{ fontSize: "0.75rem", maxWidth: "200px" }}>{h.reasonForLeaving || "-"}</td>
                        <td className="pe-4 text-end">
                          <div className="d-flex justify-content-end gap-2">
                            {/* Updated Icons to Match Educational Background */}
                            <button className="btn btn-sm border-0 shadow-none p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleEditInitiate(h)} disabled={isLoading}>
                              <i className="bi bi-pencil-square text-primary"></i>
                            </button>
                            <button className="btn btn-sm border-0 shadow-none p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleDelete(h.id)} disabled={isLoading}>
                              <i className="bi bi-trash3-fill text-danger"></i>
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                editingId !== "new" && (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted small fw-semibold">No employment history found.</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const renderEditRow = (id, formData, handleChange, handleSave, handleCancel, types, isLoading, inputStyle) => (
  <>
    <td className="ps-4 py-3">
      <input type="text" name="companyName" placeholder="Company *" value={formData.companyName} onChange={handleChange} className="form-control mb-1 fw-bold" style={inputStyle} disabled={isLoading} />
      <input type="text" name="companyAddress" placeholder="Address" value={formData.companyAddress} onChange={handleChange} className="form-control" style={{ ...inputStyle, height: "26px", fontSize: "0.7rem" }} disabled={isLoading} />
    </td>
    <td>
      <select name="employeeType" value={formData.employeeType || "Permanent"} onChange={handleChange} className="form-select fw-bold" style={inputStyle} disabled={isLoading}>
        {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
    </td>
    <td><input type="text" name="position" placeholder="Position *" value={formData.position} onChange={handleChange} className="form-control fw-bold" style={inputStyle} disabled={isLoading} /></td>
    <td>
      <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="form-control mb-1 fw-bold" style={inputStyle} disabled={isLoading} />
      <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="form-control fw-bold" style={inputStyle} disabled={isLoading} />
    </td>
    <td><span className="text-muted fw-bold" style={{ fontSize: '0.65rem' }}>Calculated</span></td>
    <td><input type="text" name="reasonForLeaving" placeholder="Reason" value={formData.reasonForLeaving} onChange={handleChange} className="form-control fw-bold" style={inputStyle} disabled={isLoading} /></td>
    <td className="pe-4 text-end">
      <div className="d-flex gap-2 justify-content-end">
        {/* Updated Edit Mode Buttons to Match Educational Background */}
        <button className="btn btn-success btn-sm p-0 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '28px', height: '28px' }} onClick={() => handleSave(id)} disabled={isLoading}>
          <i className="bi bi-check-lg"></i>
        </button>
        <button className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '28px', height: '28px' }} onClick={handleCancel} disabled={isLoading}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
    </td>
  </>
);

export default EmployeeHistory;